import { useMemo, useState } from "react"
import { useEffect } from "react"
import { Link } from "react-router-dom"
import { FileSignature, FileText, FileUp, Plus, Pencil, Trash2, AlertCircle } from "lucide-react"
import { useClientTheme } from "@/app/components/ThemeContext"
import { PageHeader } from "@/app/components/PageHeader"
import { FilterBar } from "@/app/components/FilterBar/FilterBar"
import { SearchInput } from "@/app/components/FilterBar/SearchInput"
import { SelectFilter } from "@/app/components/FilterBar/SelectFilter"
import { MultiSelectFilter } from "@/app/components/FilterBar/MultiSelectFilter"
import { TableColumnHeader } from "@/app/components/TableColumnHeader"
import { TableEmptyState } from "@/app/components/TableEmptyState"
import { DataCard } from "@/app/components/DataCard"
import { MobileSortSelect } from "@/app/components/MobileSortSelect"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { useCapability } from "@/app/hooks/useCapability"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { useTableSort } from "@/app/hooks/useTableSort"
import { stickyTh } from "@/app/lib/tableStyles"
import { primaryButton, secondaryButton } from "@/app/lib/buttonStyles"
import { DIV_RED } from "@/app/lib/txColors"
import { ErrorBanner, SuccessBanner, formatApiError } from "@/app/components/FormPrimitives"
import {
  LeaseFormModal,
  blankLeaseForm,
  type LeaseFormState,
} from "@/app/pages/LeasesPage/LeaseFormModal"
import { StrPastePanel } from "@/app/pages/LeasesPage/StrPastePanel"
import { CreateAssignmentModal } from "@/app/pages/LeasesPage/CreateAssignmentModal"
import {
  useLeases,
  useCreateLease,
  useUpdateLease,
  useDeleteLease,
  type Lease,
  type LeaseCategory,
  type LeasePayload,
  type LeaseSource,
} from "@/app/api/leases"
import type { ClientTheme } from "@/app/lib/themes"

// Prototype's amber warning tone (assignment-extraction-agent_9.10.26.jsx
// `--amber: #8A6A1F`) — flags a lease expiring within the next 90 days.
// Expired leases reuse the shared divestiture red for the same "attention"
// language the rest of the app already uses.
const AMBER = "#8A6A1F"

const CATEGORY_LABELS: Record<LeaseCategory, string> = {
  expiring: "Expiring Soon",
  active: "Active",
  expired: "Expired",
  hbp: "HBP (assumed)",
  nodate: "No Date",
}
const CATEGORY_ORDER: LeaseCategory[] = ["expiring", "active", "expired", "hbp", "nodate"]

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

function normBkPg(book: string | null, page: string | null): string {
  const b = (book ?? "").trim()
  const p = (page ?? "").trim()
  if (!b && !p) return "—"
  return `${b || "—"}/${p || "—"}`
}

function srcLabel(source: LeaseSource): string {
  const parties = [source.assignor, source.assignee].filter(Boolean).join(" → ")
  const bkPg = normBkPg(source.book, source.page)
  const bits = [parties || null, bkPg !== "—" ? bkPg : null, source.recorded_on].filter(Boolean)
  return bits.join(" · ") || source.filename || "Source"
}

function categoryColor(category: LeaseCategory, theme: ClientTheme): string {
  if (category === "expiring") return AMBER
  if (category === "expired") return DIV_RED
  return theme.textPrimary
}

// Status + expiration cell — the amber/red "needs attention" treatment other
// pages use for time-sensitive values.
function StatusCell({ lease, theme }: { lease: Lease; theme: ClientTheme }) {
  const color = categoryColor(lease.category, theme)
  const flagged = lease.category === "expiring" || lease.category === "expired"
  return (
    <div className="flex flex-col">
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "13px",
          fontWeight: flagged ? 600 : 400,
          color,
        }}
      >
        {lease.status}
      </span>
      {lease.expiration && (
        <span style={{ fontFamily: "var(--font-data)", fontSize: "11px", color: theme.textDim }}>
          exp {lease.expiration}
        </span>
      )}
    </div>
  )
}

function fieldFromLease(lease: Lease): LeaseFormState {
  return {
    lessor: lease.lessor ?? "",
    lessee: lease.lessee ?? "",
    lease_date: lease.lease_date ?? "",
    term_years: lease.term_years != null ? String(lease.term_years) : "",
    extension_years: lease.extension_years != null ? String(lease.extension_years) : "",
    extension_exercised_on: lease.extension_exercised_on ?? "",
    book: lease.book ?? "",
    page: lease.page ?? "",
    county: lease.county ?? "",
    str: lease.str ?? "",
    legal: lease.legal ?? "",
  }
}

function payloadFromForm(form: LeaseFormState): LeasePayload {
  return {
    lessor: form.lessor.trim(),
    lessee: form.lessee.trim() || null,
    lease_date: form.lease_date || null,
    term_years: form.term_years.trim() ? Number(form.term_years) : null,
    extension_years: form.extension_years.trim() ? Number(form.extension_years) : null,
    extension_exercised_on: form.extension_exercised_on || null,
    book: form.book.trim() || null,
    page: form.page.trim() || null,
    county: form.county.trim(),
    str: form.str.trim() || null,
    legal: form.legal.trim() || null,
  }
}

export function LeasesPage() {
  const { theme } = useClientTheme()
  const { canWrite, canManageUsers, isLoading: userLoading } = useCurrentUser()
  const isMobile = useIsMobile()
  // Document intake is authorize_manage! server-side: owners and superadmins.
  const canImport = useCapability("leases") && canManageUsers
  const canAssign = useCapability("assignments") && canWrite
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showAssign, setShowAssign] = useState(false)

  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, 300)
  const [strSel, setStrSel] = useState<string[]>([])
  const [category, setCategory] = useState<LeaseCategory | "">("")

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<LeaseFormState>(blankLeaseForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  // The whole schedule (unpaginated, cached) backs the STR options, the
  // "of N" total, and selection — none of which may shrink as filters narrow.
  // The unscoped-by-category fetch backs the per-category counts and shares a
  // cache key with the scoped query below whenever category is already "".
  const scheduleQuery = useLeases()
  const unscopedQuery = useLeases({ q: debouncedSearch, strs: strSel })
  const scopedQuery = useLeases(
    { q: debouncedSearch, strs: strSel, category },
    { enabled: !!category }
  )

  const schedule = scheduleQuery.data ?? []
  const allRows = unscopedQuery.data ?? []
  const rows = category ? (scopedQuery.data ?? []) : allRows
  const isLoading = category ? scopedQuery.isLoading : unscopedQuery.isLoading

  const strOptions = useMemo(() => {
    const set = new Set<string>()
    for (const r of schedule) if (r.str) set.add(r.str)
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }, [schedule])

  const categoryCounts = useMemo(() => {
    const counts: Record<LeaseCategory, number> = {
      expiring: 0,
      active: 0,
      expired: 0,
      hbp: 0,
      nodate: 0,
    }
    for (const r of allRows) counts[r.category]++
    return counts
  }, [allRows])

  const { sortKey, sortDir, handleSort, sortRows } = useTableSort<Lease>({
    rows,
    numericKeys: ["term_years", "extension_years"],
  })
  const sorted = sortRows(rows)

  // Selection survives filter changes (select across several searches, then
  // generate), so it resolves against the unfiltered schedule.
  const selectedLeases = useMemo(
    () => schedule.filter((l) => selected.has(l.id)),
    [schedule, selected]
  )
  const allVisibleSelected = sorted.length > 0 && sorted.every((l) => selected.has(l.id))
  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev)
      for (const l of sorted) {
        if (allVisibleSelected) next.delete(l.id)
        else next.add(l.id)
      }
      return next
    })

  const hasFilters = !!search || strSel.length > 0 || !!category
  const clearFilters = () => {
    setSearch("")
    setStrSel([])
    setCategory("")
  }

  const createMutation = useCreateLease()
  const updateMutation = useUpdateLease()
  const deleteMutation = useDeleteLease()
  const saveMutation = editingId ? updateMutation : createMutation

  function resetForm() {
    setForm(blankLeaseForm)
    setEditingId(null)
    setShowForm(false)
    setValidationError(null)
  }

  function startAdd() {
    setForm(blankLeaseForm)
    setEditingId(null)
    setValidationError(null)
    createMutation.reset()
    updateMutation.reset()
    setShowForm(true)
  }

  function startEdit(lease: Lease) {
    setForm(fieldFromLease(lease))
    setEditingId(lease.id)
    setValidationError(null)
    createMutation.reset()
    updateMutation.reset()
    setShowForm(true)
  }

  const set = <K extends keyof LeaseFormState>(key: K, value: LeaseFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setValidationError(null)

    if (!form.lessor.trim()) return setValidationError("Lessor is required.")
    if (!form.county.trim()) return setValidationError("County is required.")

    const payload = payloadFromForm(form)
    if (editingId) {
      updateMutation.mutate({ id: editingId, payload }, { onSuccess: resetForm })
    } else {
      createMutation.mutate(payload, { onSuccess: resetForm })
    }
  }

  if (userLoading) {
    return (
      <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
        <div
          className="max-w-[1100px]"
          style={{ color: theme.textMuted, fontFamily: "var(--font-ui)" }}
        >
          Loading…
        </div>
      </div>
    )
  }

  const colCount = (canWrite ? 9 : 8) + (canAssign ? 1 : 0)

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <PageHeader
            title="Lease Schedule"
            subtitle="Master lease schedule and expirations"
            theme={theme}
            icon={<FileSignature size={17} strokeWidth={1.75} />}
          />
          <div className="flex items-center gap-3 flex-wrap">
            {canAssign && (
              <>
                {selectedLeases.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelected(new Set())}
                    className="btn"
                    style={iconButton(theme)}
                  >
                    Clear selection
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowAssign(true)}
                  disabled={selectedLeases.length === 0}
                  className="btn"
                  style={{
                    ...primaryButton(theme),
                    opacity: selectedLeases.length > 0 ? 1 : 0.6,
                    cursor: selectedLeases.length > 0 ? "pointer" : "default",
                  }}
                  title={
                    selectedLeases.length > 0
                      ? undefined
                      : "Select leases to include in the assignment"
                  }
                >
                  <FileText size={14} /> Create Assignment
                  {selectedLeases.length > 0 ? ` (${selectedLeases.length})` : ""}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={startAdd}
              disabled={!canWrite}
              className="btn"
              style={{
                ...primaryButton(theme),
                opacity: canWrite ? 1 : 0.6,
                cursor: canWrite ? "pointer" : "default",
              }}
              title={canWrite ? undefined : "Viewers have read-only access"}
            >
              <Plus size={14} /> Add Lease
            </button>
            {canImport && (
              <Link to="/leases/intake" className="btn" style={secondaryButton(theme)}>
                <FileUp size={14} /> Import from document
              </Link>
            )}
          </div>
        </div>

        {saveMutation.isSuccess && !showForm && (
          <SuccessBanner theme={theme} message="Lease saved." />
        )}
        {deleteMutation.isSuccess && <SuccessBanner theme={theme} message="Lease deleted." />}
        {deleteMutation.isError && (
          <ErrorBanner theme={theme} message={formatApiError(deleteMutation.error)} />
        )}
        {(unscopedQuery.isError || scopedQuery.isError) && (
          <ErrorBanner
            theme={theme}
            message={formatApiError(unscopedQuery.error ?? scopedQuery.error)}
          />
        )}

        <div
          className="rounded-lg border overflow-hidden mt-2"
          style={{ borderColor: theme.borderColor, backgroundColor: theme.bgSurface }}
        >
          <FilterBar
            hasFilters={hasFilters}
            onClear={clearFilters}
            resultCount={sorted.length}
            totalCount={schedule.length}
            entityLabel="lease"
            theme={theme}
          >
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search lessor, lessee, STR, county…"
              width="260px"
              theme={theme}
            />
            <SelectFilter
              value={category}
              onChange={(v) => setCategory(v as LeaseCategory | "")}
              placeholder="All Statuses"
              options={CATEGORY_ORDER.map((c) => ({
                label: `${CATEGORY_LABELS[c]} (${categoryCounts[c]})`,
                value: c,
              }))}
              width="190px"
              theme={theme}
            />
            <MultiSelectFilter
              selected={strSel}
              onChange={setStrSel}
              placeholder="All STRs"
              options={strOptions}
              width="170px"
              theme={theme}
            />
            <StrPastePanel strOptions={strOptions} onApply={setStrSel} theme={theme} />
          </FilterBar>

          {isMobile && (
            <MobileSortSelect
              options={[
                { key: "lessor", label: "Lessor" },
                { key: "lease_date", label: "Lease Date" },
                { key: "county", label: "County" },
                { key: "str", label: "STR" },
                { key: "expiration", label: "Expiration" },
              ]}
              sortKey={sortKey as string | null}
              sortDir={sortDir}
              onSort={(k) => handleSort(k as keyof Lease)}
            />
          )}

          {isMobile ? (
            isLoading ? (
              <LoadingRow theme={theme} />
            ) : sorted.length === 0 ? (
              <EmptyBlock
                hasFilters={hasFilters}
                onClear={clearFilters}
                canImport={canImport}
                theme={theme}
              />
            ) : (
              <div className="flex flex-col gap-2.5 p-3">
                {sorted.map((lease) => (
                  <DataCard
                    key={lease.id}
                    title={lease.lessor}
                    fields={[
                      { label: "Lessee", value: lease.lessee || "—" },
                      { label: "Lease Date", value: lease.lease_date || "—", mono: true },
                      { label: "Bk/Pg", value: normBkPg(lease.book, lease.page), mono: true },
                      { label: "County", value: lease.county },
                      { label: "STR", value: lease.str || "—", mono: true },
                      { label: "Legal", full: true, value: lease.legal || "—" },
                      {
                        label: "Status",
                        full: true,
                        value: <StatusCell lease={lease} theme={theme} />,
                      },
                      {
                        label: "Sources",
                        full: true,
                        value:
                          lease.sources.length === 0 ? (
                            "—"
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              {lease.sources.map((s) => (
                                <span key={s.id}>{srcLabel(s)}</span>
                              ))}
                            </div>
                          ),
                      },
                    ]}
                    footer={
                      canWrite ? (
                        <>
                          {canAssign && (
                            <SelectBox
                              checked={selected.has(lease.id)}
                              onChange={() => toggleSelected(lease.id)}
                              label="Select"
                              theme={theme}
                            />
                          )}
                          <RowActions
                            lease={lease}
                            confirmDelete={confirmDelete}
                            setConfirmDelete={setConfirmDelete}
                            onEdit={() => startEdit(lease)}
                            onDelete={() => deleteMutation.mutate(lease.id)}
                            deleting={deleteMutation.isPending}
                            theme={theme}
                          />
                        </>
                      ) : undefined
                    }
                  />
                ))}
              </div>
            )
          ) : (
            <div className="overflow-auto" style={{ maxHeight: "70vh" }}>
              <table className="w-full border-collapse">
                <thead>
                  <tr
                    style={{
                      backgroundColor: theme.bgSurface2,
                      borderBottom: `1px solid ${theme.borderColor}`,
                    }}
                  >
                    {canAssign && (
                      <th
                        className="pl-3.5 py-2 text-left"
                        style={{ ...stickyTh(theme), width: "32px" }}
                      >
                        <SelectBox
                          checked={allVisibleSelected}
                          onChange={toggleAllVisible}
                          title="Select all shown"
                          theme={theme}
                        />
                      </th>
                    )}
                    <SortableTh
                      label="Lessor"
                      col="lessor"
                      {...{ sortKey, sortDir, handleSort, theme }}
                    />
                    <SortableTh
                      label="Lessee"
                      col="lessee"
                      {...{ sortKey, sortDir, handleSort, theme }}
                    />
                    <SortableTh
                      label="Date"
                      col="lease_date"
                      {...{ sortKey, sortDir, handleSort, theme }}
                    />
                    <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                      <TableColumnHeader label="Bk/Pg" theme={theme} />
                    </th>
                    <SortableTh
                      label="County"
                      col="county"
                      {...{ sortKey, sortDir, handleSort, theme }}
                    />
                    <SortableTh
                      label="STR"
                      col="str"
                      {...{ sortKey, sortDir, handleSort, theme }}
                    />
                    <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                      <TableColumnHeader label="Legal" theme={theme} />
                    </th>
                    <SortableTh
                      label="Status / Expiration"
                      col="expiration"
                      {...{ sortKey, sortDir, handleSort, theme }}
                    />
                    <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                      <TableColumnHeader label="Sources" theme={theme} />
                    </th>
                    {canWrite && (
                      <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                        <TableColumnHeader label="Actions" theme={theme} />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={colCount}
                        className="px-5 py-6"
                        style={{
                          fontFamily: "var(--font-ui)",
                          fontSize: "13px",
                          color: theme.textMuted,
                        }}
                      >
                        Loading…
                      </td>
                    </tr>
                  ) : sorted.length === 0 && hasFilters ? (
                    <TableEmptyState
                      message="No leases found"
                      onClear={clearFilters}
                      colSpan={colCount}
                      theme={theme}
                    />
                  ) : sorted.length === 0 ? (
                    <tr>
                      <td colSpan={colCount}>
                        <EmptyBlock
                          hasFilters={false}
                          onClear={clearFilters}
                          canImport={canImport}
                          theme={theme}
                        />
                      </td>
                    </tr>
                  ) : (
                    sorted.map((lease) => (
                      <tr key={lease.id} style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
                        {canAssign && (
                          <td className="pl-3.5 py-2">
                            <SelectBox
                              checked={selected.has(lease.id)}
                              onChange={() => toggleSelected(lease.id)}
                              title="Select for assignment"
                              theme={theme}
                            />
                          </td>
                        )}
                        <td className="px-3.5 py-2" style={cellStyle(theme, true)}>
                          {lease.lessor}
                        </td>
                        <td className="px-3.5 py-2" style={cellStyle(theme)}>
                          {lease.lessee || "—"}
                        </td>
                        <td className="px-3.5 py-2" style={dataCellStyle(theme)}>
                          {lease.lease_date || "—"}
                        </td>
                        <td className="px-3.5 py-2" style={dataCellStyle(theme)}>
                          {normBkPg(lease.book, lease.page)}
                        </td>
                        <td className="px-3.5 py-2" style={cellStyle(theme)}>
                          {lease.county}
                        </td>
                        <td className="px-3.5 py-2" style={dataCellStyle(theme)}>
                          {lease.str || "—"}
                        </td>
                        <td
                          className="px-3.5 py-2"
                          style={{ ...cellStyle(theme), maxWidth: "260px" }}
                          title={lease.legal ?? undefined}
                        >
                          <span
                            style={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {lease.legal || "—"}
                          </span>
                        </td>
                        <td className="px-3.5 py-2">
                          <StatusCell lease={lease} theme={theme} />
                        </td>
                        <td className="px-3.5 py-2" style={cellStyle(theme)}>
                          {lease.sources.length === 0 ? (
                            "—"
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              {lease.sources.map((s) => (
                                <span key={s.id} style={{ fontSize: "12px" }}>
                                  {srcLabel(s)}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        {canWrite && (
                          <td className="px-3.5 py-2">
                            <div className="flex items-center justify-end gap-2">
                              <RowActions
                                lease={lease}
                                confirmDelete={confirmDelete}
                                setConfirmDelete={setConfirmDelete}
                                onEdit={() => startEdit(lease)}
                                onDelete={() => deleteMutation.mutate(lease.id)}
                                deleting={deleteMutation.isPending}
                                theme={theme}
                              />
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <LeaseFormModal
          editing={!!editingId}
          form={form}
          set={set}
          onSubmit={handleSubmit}
          onClose={resetForm}
          saving={saveMutation.isPending}
          error={
            validationError ?? (saveMutation.isError ? formatApiError(saveMutation.error) : null)
          }
          theme={theme}
        />
      )}

      {showAssign && selectedLeases.length > 0 && (
        <CreateAssignmentModal
          leases={selectedLeases}
          onClose={() => setShowAssign(false)}
          theme={theme}
        />
      )}
    </div>
  )
}

function SelectBox({
  checked,
  onChange,
  label,
  title,
  theme,
}: {
  checked: boolean
  onChange: () => void
  label?: string
  title?: string
  theme: ClientTheme
}) {
  return (
    <label
      className="flex items-center gap-1.5"
      style={{
        cursor: "pointer",
        fontFamily: "var(--font-ui)",
        fontSize: "12px",
        color: theme.textMuted,
      }}
      title={title}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ accentColor: theme.brandAccent }}
      />
      {label}
    </label>
  )
}

function SortableTh({
  label,
  col,
  sortKey,
  sortDir,
  handleSort,
  theme,
}: {
  label: string
  col: keyof Lease
  sortKey: keyof Lease | null
  sortDir: "asc" | "desc"
  handleSort: (key: keyof Lease) => void
  theme: ClientTheme
}) {
  const active = sortKey === col
  return (
    <th
      className="px-3.5 py-2 text-left"
      style={{ ...stickyTh(theme), cursor: "pointer" }}
      onClick={() => handleSort(col)}
    >
      <span className="flex items-center gap-1">
        <TableColumnHeader label={label} theme={theme} />
        {active && (
          <span style={{ fontSize: "10px", color: theme.textDim }}>
            {sortDir === "asc" ? "▲" : "▼"}
          </span>
        )}
      </span>
    </th>
  )
}

function RowActions({
  lease,
  confirmDelete,
  setConfirmDelete,
  onEdit,
  onDelete,
  deleting,
  theme,
}: {
  lease: Lease
  confirmDelete: string | null
  setConfirmDelete: (id: string | null) => void
  onEdit: () => void
  onDelete: () => void
  deleting: boolean
  theme: ClientTheme
}) {
  return (
    <>
      <button type="button" onClick={onEdit} className="btn" style={iconButton(theme)} title="Edit">
        <Pencil size={13} /> Edit
      </button>
      {confirmDelete === lease.id ? (
        <span className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="btn"
            style={{ ...iconButton(theme), color: theme.brandAccent }}
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(null)}
            className="btn"
            style={iconButton(theme)}
          >
            Cancel
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmDelete(lease.id)}
          className="btn"
          style={{ ...iconButton(theme), color: theme.brandAccent }}
          title="Delete"
        >
          <Trash2 size={13} /> Delete
        </button>
      )}
    </>
  )
}

function LoadingRow({ theme }: { theme: ClientTheme }) {
  return (
    <div
      className="px-5 py-6"
      style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textMuted }}
    >
      Loading…
    </div>
  )
}

function EmptyBlock({
  hasFilters,
  onClear,
  canImport,
  theme,
}: {
  hasFilters: boolean
  onClear: () => void
  canImport: boolean
  theme: ClientTheme
}) {
  return (
    <div className="px-5 py-10 text-center flex flex-col items-center gap-2">
      <AlertCircle size={20} style={{ color: theme.textDim }} />
      <span style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textMuted }}>
        {hasFilters ? (
          "No leases found"
        ) : canImport ? (
          <>
            Schedule is empty —{" "}
            <Link to="/leases/intake" style={{ color: theme.brandAccent }}>
              import a document
            </Link>{" "}
            or add a lease
          </>
        ) : (
          "Schedule is empty — import a backup or add a lease"
        )}
      </span>
      {hasFilters && (
        <button
          onClick={onClear}
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "12px",
            color: theme.brandAccent,
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          Clear all filters
        </button>
      )}
    </div>
  )
}

function cellStyle(theme: ClientTheme, bold = false): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    fontWeight: bold ? 600 : 400,
    color: bold ? theme.textPrimary : theme.textMuted,
  }
}

function dataCellStyle(theme: ClientTheme): React.CSSProperties {
  return { fontFamily: "var(--font-data)", fontSize: "13px", color: theme.textPrimary }
}

function iconButton(theme: ClientTheme): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontFamily: "var(--font-ui)",
    fontSize: "12px",
    fontWeight: 500,
    padding: "5px 9px",
    borderRadius: "7px",
    backgroundColor: theme.bgSurface2,
    color: theme.textMuted,
    border: `1px solid ${theme.borderColor}`,
    cursor: "pointer",
  }
}
